import { useAuth } from '@/stores/auth';

/**
 * Unsent composer text per channel/DM. Kept in memory for instant access and
 * mirrored to localStorage (namespaced by user, debounced) so a refresh or a
 * crashed tab doesn't lose a half-written message. Explicit logout wipes it.
 */

const PREFIX = 'clanfire:drafts:';
const MAX_AGE_MS = 30 * 86_400_000;
const MAX_ENTRIES = 200;

type Entry = { text: string; at: number };

let owner: string | null = null;
let cache = new Map<string, Entry>();
let dirty = false;
let timer: ReturnType<typeof setTimeout> | undefined;

function load(userId: string): Map<string, Entry> {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFIX + userId) ?? '{}') as Record<string, Entry>;
    const cutoff = Date.now() - MAX_AGE_MS;
    return new Map(Object.entries(raw).filter(([, e]) => typeof e?.text === 'string' && e.at > cutoff));
  } catch {
    return new Map();
  }
}

function persist() {
  clearTimeout(timer);
  if (!dirty || !owner) return;
  dirty = false;
  // Keep only the most recent drafts so storage can't grow without bound.
  const entries = [...cache.entries()].sort((a, b) => b[1].at - a[1].at).slice(0, MAX_ENTRIES);
  try {
    if (entries.length) localStorage.setItem(PREFIX + owner, JSON.stringify(Object.fromEntries(entries)));
    else localStorage.removeItem(PREFIX + owner);
  } catch {
    /* Storage full or unavailable — drafts still live in memory. */
  }
}

function sync() {
  const id = useAuth.getState().user?.id ?? null;
  if (id === owner) return;
  persist();
  owner = id;
  cache = id ? load(id) : new Map();
}

export function getDraft(key: string): string {
  sync();
  return cache.get(key)?.text ?? '';
}

export function setDraft(key: string, text: string) {
  sync();
  if (text) cache.set(key, { text, at: Date.now() });
  else if (!cache.delete(key)) return;
  dirty = true;
  clearTimeout(timer);
  timer = setTimeout(persist, 400);
}

/** Forgets every draft for the signed-in user (used on explicit logout). */
export function clearDrafts() {
  clearTimeout(timer);
  if (owner) {
    try {
      localStorage.removeItem(PREFIX + owner);
    } catch {
      /* ignore */
    }
  }
  cache = new Map();
  dirty = false;
  owner = null;
}

if (typeof window !== 'undefined') {
  // Write pending changes before the page goes away.
  window.addEventListener('pagehide', persist);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') persist();
  });
}
